import { MutableRefObject, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useOccupationalMedicineStyles from "../OccupationalMedicineFormStyles";
import { useAppDispatch } from "../../../hooks/customReduxHooks";
import { isNotNullOrUndefined } from "../../../utils/lang";

import { Box, Button, Checkbox, FormControlLabel, FormGroup, FormHelperText, Grid, Link } from "@material-ui/core";
import { CheckCircleOutline } from "@material-ui/icons";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import i18nNamespaces from "../../../const/i18nNamespaces";
import { AccordionDetails, Accordion, AccordionSummary } from "../../common/Accordion/Accordion";
import { useFormik } from "formik";
import * as yup from 'yup';
import PatientPlaceOfWorkCreateItem from "./PatientPlaceOfWorkCreateItem";
import { PatientPlaceOfWorkDto, PatientPlaceOfWorkHistoryDto } from "../../../types/occupationalMedicine";
import PatientPlaceOfWorkItem from "./PatientPlaceOfWorkItem";
import { RenderReturnNextButtons } from "../CommonView";
import useStyles from "./PatientPlaceOfWorkItemStyles";
import { fetchSection3Data } from "../../../store/occupationalMedicineSlice";
import { useParams } from "react-router";

const SECTION = 3;


export type Props = {
    currentExpanded:number
    data: PatientPlaceOfWorkHistoryDto,
    onSubmitSection: (values:string, fetchDataFunction:any, currentSection:number, stayOnCurrent?:boolean) =>void
    onReturn: () => void;
    refSection:MutableRefObject<any>
}
const PatientPlaceOfWorkComponent = ({currentExpanded,data, onSubmitSection, onReturn, refSection}:Props) =>
{
    const {t} = useTranslation(i18nNamespaces.OCCUPATIONAL_MEDICINE);
    const {t:commonT} = useTranslation(i18nNamespaces.COMMON);
    const classes = useOccupationalMedicineStyles();
    const customClasses = useStyles();
    const {appointmentId} = useParams<{appointmentId: string}>();
    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [isAddOrEditMode, setIsAddOrEditMode] = useState<boolean>(false);
    const [dataToEdit, setDataToEdit] = useState<PatientPlaceOfWorkDto>(null);
    const [indexToEdit, setIndexToEdit] = useState<number>(null);

    useEffect(()=>{
        if(isNotNullOrUndefined(data))
            setIsSaved(true);
    }, [data]);

    
    
    const onSubmit = (newPlace: PatientPlaceOfWorkDto, stayOnCurrent=false) => {
        if (newPlace===null){
            if(data===null)
                onSubmitSection(JSON.stringify({placesOfWork: []}), fetchSection3Data({id:appointmentId}), SECTION, stayOnCurrent);   
            else
                onSubmitSection(JSON.stringify(data), fetchSection3Data({id:appointmentId}), SECTION, stayOnCurrent);   
        }

        //if editing
        else if (isNotNullOrUndefined(indexToEdit)){
            const updatedPlaces = [...data.placesOfWork.slice(0, indexToEdit), ... data.placesOfWork.slice(indexToEdit+1)]
            const resultPlaces = [...updatedPlaces, newPlace];
            onSubmitSection(JSON.stringify({placesOfWork: resultPlaces}),fetchSection3Data({id:appointmentId}), SECTION,stayOnCurrent);
        }
        //if new data
        else{
            if (data!== null){
                const updatedPlaces = [
                    ...data.placesOfWork,
                    newPlace,  
                ];
                onSubmitSection(JSON.stringify({placesOfWork: updatedPlaces}), fetchSection3Data({id:appointmentId}), SECTION, stayOnCurrent);
            }
            else{
                const places = [newPlace];
                onSubmitSection(JSON.stringify({placesOfWork: places}), fetchSection3Data({id:appointmentId}), SECTION,stayOnCurrent);              
            }
        }
        setDataToEdit(null);
        setIndexToEdit(null);
    }

    const onCancel = () => {
        setIsAddOrEditMode(false);
        setDataToEdit(null);
        setIndexToEdit(null);
    }

    const onDeletePosition = (index:number) =>{
        const updatedPlaces = [...data.placesOfWork.slice(0, index), ... data.placesOfWork.slice(index+1)]
        onSubmitSection(JSON.stringify({placesOfWork: updatedPlaces}),fetchSection3Data({id:appointmentId}), SECTION,true);
    }

    const onEditPosition = (index: number, data:PatientPlaceOfWorkDto) => {
        setIndexToEdit(index);
        setDataToEdit(data);
        setIsAddOrEditMode(true);
    }
      
    return (
        <Accordion square defaultExpanded={currentExpanded===SECTION} className={classes.container} key={SECTION+currentExpanded} ref={refSection}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}
                            aria-controls="panel1a-content"
                            id="panel1a-header"
                            >

                <Box className={classes.titleContainer}>
                    <h4 className={classes.sectionHeader}>
                        {t('previousEmployment')}
                    </h4>

                    {isSaved && <CheckCircleOutline className={classes.alertIcon}/>}
                </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.detailsContainer}>
                {!isAddOrEditMode &&
                    <Grid container spacing={1}>
                        {
                            data && data.placesOfWork.map((v, i) => (
                                <Grid item xs={6} key={i}>
                                    <PatientPlaceOfWorkItem 
                                        index={i}
                                        data={v}
                                        onDelete={onDeletePosition}
                                        onEdit={onEditPosition}/>
                                </Grid>
                            ))
                        }
                        <Grid item xs={12}>
                            <Link                                                    
                                className={customClasses.link}
                                onClick={() => {setIsAddOrEditMode(true)}}
                                >
                                 {t('addPosition')}
                                   
                            </Link>  
                        </Grid>
                        <Grid container>
                            <Grid item xs={6}>
                                <Button
                                    onClick={onReturn}
                                    className={classes.buttonReturn}
                                    variant={'outlined'}
                                    color={'secondary'}>
                                    {t('return')}
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button
                                    onClick={() => {onSubmit(null, false)}}
                                    className={classes.buttonNext}
                                    variant={'contained'}
                                    color={'secondary'}>
                                    {t('next')}
                                </Button>
                            </Grid>
                        </Grid>

                    </Grid>
                }             
                {isAddOrEditMode && <PatientPlaceOfWorkCreateItem 
                    data={dataToEdit} 
                    onSubmitSection={onSubmit}
                    onCancel={onCancel}/>}
            </AccordionDetails>
        </Accordion>
    
    )
}

export default PatientPlaceOfWorkComponent;